import { useCallback, useEffect, useState } from "react";
import {
	Badge,
	Banner,
	BlockStack,
	Box,
	Card,
	Checkbox,
	Divider,
	InlineGrid,
	InlineStack,
	Layout,
	Page,
	Select,
	SkeletonBodyText,
	SkeletonPage,
	Text,
	TextField,
} from "@shopify/polaris";
import { authenticatedFetch } from "../api";
import { AppBridgeSaveBar } from "../components/AppBridgeSaveBar";
import { CatalogMultiSelect } from "../components/CatalogMultiSelect";
import type { CatalogFacets, TranslationConfig } from "../types";

type Tier = "basic" | "advanced" | "pro";

const TIER_INFO: Record<Tier, { title: string; subtitle: string; description: string }> = {
	basic: {
		title: "Basic",
		subtitle: "Essentials",
		description: "Translate products that have images and descriptions. No extra configuration needed.",
	},
	advanced: {
		title: "Advanced",
		subtitle: "Targeted",
		description: "Filter by product status, type, vendor, and tags to control exactly which products get translated.",
	},
	pro: {
		title: "Pro",
		subtitle: "Full control",
		description: "All filters including date ranges and price thresholds for maximum precision.",
	},
};

const TIER_ORDER: Tier[] = ["basic", "advanced", "pro"];

function TierCard({
	tier,
	selected,
	onSelect,
}: {
	tier: Tier;
	selected: boolean;
	onSelect: () => void;
}) {
	const info = TIER_INFO[tier];

	return (
		<div
			onClick={onSelect}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onSelect();
				}
			}}
			style={{
				cursor: "pointer",
				borderRadius: "12px",
				border: selected
					? "2px solid var(--p-color-border-emphasis)"
					: "2px solid var(--p-color-border)",
				background: selected
					? "var(--p-color-bg-surface-selected)"
					: "var(--p-color-bg-surface)",
				transition: "border-color 0.15s, background 0.15s",
			}}
		>
			<Box padding="400">
				<BlockStack gap="300">
					<InlineStack align="space-between" blockAlign="center">
						<BlockStack gap="100">
							<Text as="h3" variant="headingMd">
								{info.title}
							</Text>
							<Text as="p" variant="bodySm" tone="subdued">
								{info.subtitle}
							</Text>
						</BlockStack>
						{selected && <Badge tone="success">Selected</Badge>}
					</InlineStack>
					<Text as="p" variant="bodyMd" tone="subdued">
						{info.description}
					</Text>
				</BlockStack>
			</Box>
		</div>
	);
}

function parseTags(value: string): string[] {
	return value
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
}

function parseNum(value: string): number | null {
	const n = parseFloat(value);
	return isNaN(n) ? null : n;
}

function parseIntNum(value: string): number | null {
	const n = parseInt(value);
	return isNaN(n) ? null : n;
}

export default function AutoTranslate() {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	// Original config for discard
	const [originalConfig, setOriginalConfig] = useState<TranslationConfig | null>(null);

	// Auto-translate state
	const [enabled, setEnabled] = useState(false);
	const [tier, setTier] = useState<Tier>("basic");

	// Basic tier
	const [requireDescription, setRequireDescription] = useState(true);
	const [requireImages, setRequireImages] = useState(true);

	// Advanced tier
	const [status, setStatus] = useState("");
	const [vendors, setVendors] = useState<string[]>([]);
	const [productTypes, setProductTypes] = useState<string[]>([]);
	const [includeTags, setIncludeTags] = useState("");
	const [excludeTags, setExcludeTags] = useState("");

	// Pro tier
	const [vendorsExclude, setVendorsExclude] = useState<string[]>([]);
	const [productTypesExclude, setProductTypesExclude] = useState<string[]>([]);
	const [createdWithinDays, setCreatedWithinDays] = useState("");
	const [updatedWithinDays, setUpdatedWithinDays] = useState("");
	const [minPrice, setMinPrice] = useState("");

	// Catalog facets (vendors + product types)
	const [catalogVendors, setCatalogVendors] = useState<string[] | null>(null);
	const [catalogProductTypes, setCatalogProductTypes] = useState<string[] | null>(null);
	const [catalogLoading, setCatalogLoading] = useState(true);
	const [catalogFailed, setCatalogFailed] = useState(false);

	const markDirty = useCallback(() => {
		setIsDirty(true);
		setSaveSuccess(false);
	}, []);

	function applyConfig(config: TranslationConfig) {
		setOriginalConfig(config);
		const at = config.auto_translate;
		setEnabled(at?.enabled ?? false);
		setTier(at?.tier ?? "basic");

		const rules = at?.rules ?? {};
		setRequireDescription(rules.require_description ?? true);
		setRequireImages(rules.require_images ?? true);
		setStatus(rules.status ?? "");
		// Prefer new arrays; fall back to legacy singletons for pre-migration tenants.
		setVendors(
			rules.vendors && rules.vendors.length > 0
				? rules.vendors
				: rules.vendor
					? [rules.vendor]
					: [],
		);
		setVendorsExclude(rules.vendors_exclude ?? []);
		setProductTypes(
			rules.product_types && rules.product_types.length > 0
				? rules.product_types
				: rules.product_type
					? [rules.product_type]
					: [],
		);
		setProductTypesExclude(rules.product_types_exclude ?? []);
		setIncludeTags((rules.include_tags ?? []).join(", "));
		setExcludeTags((rules.exclude_tags ?? []).join(", "));
		setCreatedWithinDays(rules.created_within_days?.toString() ?? "");
		setUpdatedWithinDays(rules.updated_within_days?.toString() ?? "");
		setMinPrice(rules.min_price?.toString() ?? "");
	}

	useEffect(() => {
		Promise.allSettled([
			authenticatedFetch<TranslationConfig>("/api/config"),
			authenticatedFetch<CatalogFacets>("/api/catalog-facets"),
		]).then(([configRes, facetsRes]) => {
			if (configRes.status === "fulfilled") {
				applyConfig(configRes.value);
			} else {
				setError(configRes.reason?.message ?? "Failed to load configuration");
			}

			if (facetsRes.status === "fulfilled") {
				setCatalogVendors(facetsRes.value.vendors);
				setCatalogProductTypes(facetsRes.value.productTypes);
			} else {
				setCatalogFailed(true);
				setCatalogVendors(null);
				setCatalogProductTypes(null);
			}

			setCatalogLoading(false);
			setLoading(false);
		});
	}, []);

	function handleTierChange(newTier: Tier) {
		const currentIndex = TIER_ORDER.indexOf(tier);
		const newIndex = TIER_ORDER.indexOf(newTier);

		setTier(newTier);
		markDirty();

		if (newIndex < currentIndex) {
			if (newIndex < 2) {
				// Pro → lower: drop exclude lists and collapse includes to a single value
				setVendorsExclude([]);
				setProductTypesExclude([]);
				setVendors((prev) => prev.slice(0, 1));
				setProductTypes((prev) => prev.slice(0, 1));
				setCreatedWithinDays("");
				setUpdatedWithinDays("");
				setMinPrice("");
			}
			if (newIndex < 1) {
				// Advanced → Basic: clear all filter settings
				setStatus("");
				setVendors([]);
				setProductTypes([]);
				setIncludeTags("");
				setExcludeTags("");
			}
		}
	}

	function handleDiscard() {
		if (originalConfig) applyConfig(originalConfig);
		setIsDirty(false);
		setSaveSuccess(false);
	}

	async function handleSave() {
		setSaving(true);
		setSaveError(null);
		setSaveSuccess(false);

		const rules: Record<string, unknown> = {
			require_description: requireDescription,
			require_images: requireImages,
		};

		if (tier === "advanced" || tier === "pro") {
			rules.status = status || undefined;
			rules.vendors = vendors;
			rules.product_types = productTypes;
			rules.include_tags = includeTags ? parseTags(includeTags) : [];
			rules.exclude_tags = excludeTags ? parseTags(excludeTags) : [];
		}

		if (tier === "pro") {
			rules.vendors_exclude = vendorsExclude;
			rules.product_types_exclude = productTypesExclude;
			rules.created_within_days = parseIntNum(createdWithinDays);
			rules.updated_within_days = parseIntNum(updatedWithinDays);
			rules.min_price = parseNum(minPrice);
		}

		try {
			await authenticatedFetch("/api/config", {
				method: "PUT",
				body: JSON.stringify({
					auto_translate: {
						enabled,
						tier,
						rules,
					},
				}),
			});
			setIsDirty(false);
			setSaveSuccess(true);
		} catch (err: unknown) {
			setSaveError(err instanceof Error ? err.message : "Save failed");
		} finally {
			setSaving(false);
		}
	}

	if (loading) {
		return (
			<SkeletonPage title="Auto-Translate">
				<Layout>
					<Layout.Section>
						<Card>
							<SkeletonBodyText lines={3} />
						</Card>
					</Layout.Section>
					<Layout.Section>
						<InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
							{TIER_ORDER.map((t) => (
								<Card key={t}>
									<SkeletonBodyText lines={4} />
								</Card>
							))}
						</InlineGrid>
					</Layout.Section>
				</Layout>
			</SkeletonPage>
		);
	}

	if (originalConfig?.trigger_mode === "sync-safe") {
		const triggerTag = originalConfig.filters?.tag_pending || "translate-pending";
		return (
			<Page title="Auto-Translate">
				<Layout>
					<Layout.Section>
						<Banner tone="info" title="Translations managed from your content manager">
							<p>
								Auto-Translate is not available for this shop. It adds tags, and your
								content manager replaces the tags at each Push to Shopify. To translate
								a product, add the <code>{triggerTag}</code> tag in your content
								manager and use Push to Shopify.
							</p>
						</Banner>
					</Layout.Section>
				</Layout>
			</Page>
		);
	}

	return (
		<Page
			title="Auto-Translate"
			subtitle="Automatically translate products based on rules — no tags needed."
		>
			<AppBridgeSaveBar
				id="auto-translate-save-bar"
				open={isDirty}
				discardConfirmation
			>
				<button variant="primary" loading={saving || undefined} onClick={handleSave} />
				<button onClick={handleDiscard} />
			</AppBridgeSaveBar>
			<Layout>
				{saveSuccess && (
					<Layout.Section>
						<Banner
							tone="success"
							title="Auto-translate settings saved"
							onDismiss={() => setSaveSuccess(false)}
						/>
					</Layout.Section>
				)}

				{error && (
					<Layout.Section>
						<Banner tone="critical" title="Failed to load configuration">
							<p>{error}</p>
						</Banner>
					</Layout.Section>
				)}

				{saveError && (
					<Layout.Section>
						<Banner
							tone="critical"
							title="Failed to save"
							onDismiss={() => setSaveError(null)}
						>
							<p>{saveError}</p>
						</Banner>
					</Layout.Section>
				)}

				<Layout.Section>
					<Card>
						<BlockStack gap="400">
							<Checkbox
								label="Enable auto-translate"
								checked={enabled}
								onChange={(v) => {
									setEnabled(v);
									markDirty();
								}}
								helpText="When enabled, products matching your rules will be automatically translated on each scheduled run."
							/>
							{enabled && (
								<Banner tone="info">
									<p>
										Products matching <strong>all</strong> conditions will be
										translated automatically. The{" "}
										<code>translate-pending</code> tag is not needed.
									</p>
								</Banner>
							)}
						</BlockStack>
					</Card>
				</Layout.Section>

				{enabled && (
					<>
						<Layout.Section>
							<BlockStack gap="300">
								<Text as="h2" variant="headingMd">
									Automation level
								</Text>
								<Text as="p" variant="bodyMd" tone="subdued">
									Choose how much control you want over which products get
									translated.
								</Text>
								<InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
									{TIER_ORDER.map((t) => (
										<TierCard
											key={t}
											tier={t}
											selected={tier === t}
											onSelect={() => handleTierChange(t)}
										/>
									))}
								</InlineGrid>
							</BlockStack>
						</Layout.Section>

						<Layout.AnnotatedSection
							title="Content requirements"
							description="Ensure products have the essential content before translating them."
						>
							<Card>
								<BlockStack gap="300">
									<Checkbox
										label="Require description"
										checked={requireDescription}
										onChange={(v) => {
											setRequireDescription(v);
											markDirty();
										}}
										helpText="Only translate products that have a non-empty description."
									/>
									<Checkbox
										label="Require images"
										checked={requireImages}
										onChange={(v) => {
											setRequireImages(v);
											markDirty();
										}}
										helpText="Only translate products that have at least one image."
									/>
								</BlockStack>
							</Card>
						</Layout.AnnotatedSection>

						{(tier === "advanced" || tier === "pro") && (
							<Layout.AnnotatedSection
								title="Product filters"
								description="Narrow down which products get translated based on their attributes."
							>
								<Card>
									<BlockStack gap="400">
										<Select
											label="Product status"
											options={[
												{ label: "Any status", value: "" },
												{ label: "Active", value: "active" },
												{ label: "Draft", value: "draft" },
												{ label: "Archived", value: "archived" },
											]}
											value={status}
											onChange={(v) => {
												setStatus(v);
												markDirty();
											}}
										/>

										<Divider />

										{catalogFailed && (
											<Banner tone="warning" title="Catalog lookup failed">
												<p>
													We couldn't load your vendors and product types.
													Type values manually below; they'll still match
													products exactly.
												</p>
											</Banner>
										)}

										{tier === "advanced" && (
											<>
												<CatalogMultiSelect
													label="Product type"
													options={catalogProductTypes}
													loading={catalogLoading}
													selected={productTypes}
													onChange={(next) => {
														setProductTypes(next);
														markDirty();
													}}
													placeholder="e.g. T-Shirt"
													helpText="Only translate products of this type."
												/>

												<CatalogMultiSelect
													label="Vendor"
													options={catalogVendors}
													loading={catalogLoading}
													selected={vendors}
													onChange={(next) => {
														setVendors(next);
														markDirty();
													}}
													placeholder="e.g. Nike"
													helpText="Only translate products from this vendor."
												/>

												<Divider />
											</>
										)}

										<TextField
											label="Include tags"
											value={includeTags}
											onChange={(v) => {
												setIncludeTags(v);
												markDirty();
											}}
											autoComplete="off"
											placeholder="e.g. ready-for-export, featured"
											helpText="Only translate products that have ALL of these tags. Separate with commas."
										/>

										<TextField
											label="Exclude tags"
											value={excludeTags}
											onChange={(v) => {
												setExcludeTags(v);
												markDirty();
											}}
											autoComplete="off"
											placeholder="e.g. no-translate, coming-soon"
											helpText="Skip products that have ANY of these tags. Separate with commas."
										/>
									</BlockStack>
								</Card>
							</Layout.AnnotatedSection>
						)}

						{tier === "pro" && (
							<Layout.AnnotatedSection
								title="Vendor & type targeting"
								description="Include or exclude multiple vendors and product types."
							>
								<Card>
									<BlockStack gap="400">
										<CatalogMultiSelect
											label="Include vendors"
											options={catalogVendors}
											loading={catalogLoading}
											selected={vendors}
											onChange={(next) => {
												setVendors(next);
												markDirty();
											}}
											multiple
											helpText="Translate products from ANY of these vendors."
										/>
										<CatalogMultiSelect
											label="Exclude vendors"
											options={catalogVendors}
											loading={catalogLoading}
											selected={vendorsExclude}
											onChange={(next) => {
												setVendorsExclude(next);
												markDirty();
											}}
											multiple
											helpText="Skip products from ANY of these vendors."
										/>

										<Divider />

										<CatalogMultiSelect
											label="Include product types"
											options={catalogProductTypes}
											loading={catalogLoading}
											selected={productTypes}
											onChange={(next) => {
												setProductTypes(next);
												markDirty();
											}}
											multiple
											helpText="Translate products of ANY of these types."
										/>
										<CatalogMultiSelect
											label="Exclude product types"
											options={catalogProductTypes}
											loading={catalogLoading}
											selected={productTypesExclude}
											onChange={(next) => {
												setProductTypesExclude(next);
												markDirty();
											}}
											multiple
											helpText="Skip products of ANY of these types."
										/>
									</BlockStack>
								</Card>
							</Layout.AnnotatedSection>
						)}

						{tier === "pro" && (
							<Layout.AnnotatedSection
								title="Advanced filters"
								description="Fine-tune with date ranges and price thresholds."
							>
								<Card>
									<BlockStack gap="400">
										<InlineStack gap="400">
											<div style={{ flex: 1 }}>
												<TextField
													label="Created within (days)"
													type="number"
													value={createdWithinDays}
													onChange={(v) => {
														setCreatedWithinDays(v);
														markDirty();
													}}
													autoComplete="off"
													placeholder="e.g. 30"
													helpText="Only products created in the last N days."
												/>
											</div>
											<div style={{ flex: 1 }}>
												<TextField
													label="Updated within (days)"
													type="number"
													value={updatedWithinDays}
													onChange={(v) => {
														setUpdatedWithinDays(v);
														markDirty();
													}}
													autoComplete="off"
													placeholder="e.g. 7"
													helpText="Only products updated in the last N days."
												/>
											</div>
										</InlineStack>

										<TextField
											label="Minimum price"
											type="number"
											value={minPrice}
											onChange={(v) => {
												setMinPrice(v);
												markDirty();
											}}
											autoComplete="off"
											prefix="$"
											placeholder="0.00"
											helpText="Skip products where all variants are below this price."
										/>
									</BlockStack>
								</Card>
							</Layout.AnnotatedSection>
						)}
					</>
				)}

			</Layout>
		</Page>
	);
}
